/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import { useEffect, useState } from "react";
import { getDashboardCount } from "api";
import { NumberFormatter } from "utilities";
import PieChart from "examples/Charts/PieChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import { getMonthlyAppliedJobCounts } from "api";
import { getCompanyCountByIndustry } from "api";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";

function Dashboard() {
  // const { sales, tasks } = reportsLineChartData;
  const [count, setCount] = useState();
  const [monthlyCount, setMonthlyCount] = useState([]);
  const [industryCount, setIndustryCount] = useState([]);

  useEffect(() => {
    getDashboardCountApi();
    getMonthlyAppliedJobCountsApi();
    getCompanyCountByIndustryApi();
  }, []);

  const getDashboardCountApi = () => {
    var params = {};

    getDashboardCount({
      params: params,
    })
      .then((responseJson) => {
        if (responseJson) {
          setCount(responseJson);
        }
      })
      .catch((e) => {});
  };

  const getMonthlyAppliedJobCountsApi = () => {
    var params = {};

    getMonthlyAppliedJobCounts({
      params: params,
    })
      .then((responseJson) => {
        if (responseJson) {
          const counts = responseJson?.jobs.map((item) => item.count);
          setMonthlyCount(counts);
        }
      })
      .catch((e) => {});
  };

  const getCompanyCountByIndustryApi = () => {
    var params = {};

    getCompanyCountByIndustry({
      params: params,
    })
      .then((responseJson) => {
        if (responseJson) {
          const counts = responseJson?.industry.map((item) => item.count);
          const industry = responseJson?.industry.map((item) => item.industryName);
          setIndustryCount({ name: industry, count: counts });
        }
      })
      .catch((e) => {});
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title="Dashboard" />
      <MDBox py={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="person"
                title=""
                count={NumberFormatter(parseInt(count?.employee) || 0)}
                percentage={{
                  color: "secondary",
                  amount: "Registered Employees",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="badge"
                title=""
                count={NumberFormatter(parseInt(count?.company) || 0)}
                percentage={{
                  color: "secondary",
                  amount: "Registered Employers",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="work"
                title=""
                count={NumberFormatter(parseInt(count?.job) || 0)}
                percentage={{
                  color: "secondary",
                  amount: "Total Job Posted",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="article_outlined"
                title=""
                count={NumberFormatter(parseFloat(count?.avgApplicant) || 0, 2)}
                percentage={{
                  color: "secondary",
                  amount: "Avg. Applications Per Job Post",
                  label: "",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <PieChart
                  icon={{ color: "info", component: "pie_chart" }}
                  title="Total Jobs"
                  description="."
                  height={"19.125rem"}
                  chart={{
                    labels: ["Active", "Pending", "Closed"],
                    datasets: {
                      label: " Jobs",
                      backgroundColors: ["success", "secondary", "warning"],
                      data: [count?.jobCountActive, count?.jobCountPending, count?.jobCountClosed],
                    },
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <DefaultLineChart
                  icon={{ color: "dark", component: "timeline" }}
                  color="success"
                  title={"Monthly"}
                  description={"Job vs Application Trend"}
                  // date="updated 4 min ago"
                  height={"19.125rem"}
                  chart={{
                    labels: [
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ],
                    datasets: [
                      {
                        label: " Applicants",
                        color: "info",
                        data: monthlyCount,
                      },
                    ],
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <DefaultDoughnutChart
                  icon={{ color: "info", component: "donut_large" }}
                  title="Industry Wise"
                  description="Companies"
                  height={"19.125rem"}
                  chart={{
                    labels: industryCount?.name,
                    datasets: {
                      label: " Industry",
                      backgroundColors: [
                        "secondary",
                        "info",
                        "warning",
                        "success",
                        "error",
                        "light",
                        "dark",
                        // "text",
                      ],
                      data: industryCount?.count,
                    },
                  }}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        {/* <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <Projects />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <OrdersOverview />
            </Grid>
          </Grid>
        </MDBox> */}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
